import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function AddToScenarioDialog({ item, onClose }) {
  const [drafts, setDrafts] = useState(null);
  const [target, setTarget] = useState("new");
  const [scaffold, setScaffold] = useState(true);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/scenarios")
      .then(({ data }) => setDrafts(data.filter((s) => s.status === "draft")))
      .catch(() => setDrafts([]));
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/scenarios/from-wishlist", {
        wishlist_item_id: item.id,
        scenario_id: target === "new" ? null : target,
        scaffold,
      });
      const cost = data.totals?.cost;
      toast.success(`Scenario "${data.name}" ready${cost != null ? ` · est $${Number(cost).toLocaleString()}` : ""}`);
      (data.scaffold_notes || []).slice(0, 2).forEach((n) => toast.message(n));
      onClose();
      navigate("/plan/scenarios");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create scenario");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none border-sand-300" data-testid="add-to-scenario-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl tracking-tight">
            Scope the real cost
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-sand-600">
            <span className="font-medium text-sand-900">"{item.title}"</span> becomes a scenario with the
            fully-loaded cost — flights priced live from wherever you'll be, plus a stay estimate.
          </p>

          <div className="space-y-2">
            <label className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${target === "new" ? "border-ember bg-ember/5" : "border-sand-200 hover:border-sand-400"}`}>
              <input type="radio" name="scenario-target" checked={target === "new"} onChange={() => setTarget("new")} className="accent-ember" data-testid="scenario-target-new" />
              <span className="text-sm font-medium">New scenario · "{item.title}"</span>
            </label>
            {(drafts || []).map((s) => (
              <label key={s.id} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${target === s.id ? "border-ember bg-ember/5" : "border-sand-200 hover:border-sand-400"}`} data-testid={`scenario-target-${s.id}`}>
                <input type="radio" name="scenario-target" checked={target === s.id} onChange={() => setTarget(s.id)} className="accent-ember" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{s.name}</span>
                  <span className="block font-mono text-[11px] text-sand-500">
                    {s.totals?.cost != null ? `est $${Number(s.totals.cost).toLocaleString()}` : "unpriced"}
                    {s.totals?.start_date ? ` · ${s.totals.start_date}` : ""}
                  </span>
                </span>
              </label>
            ))}
          </div>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="scaffold-toggle">
            <input type="checkbox" checked={scaffold} onChange={(e) => setScaffold(e.target.checked)} className="mt-0.5 w-4 h-4 accent-ember" />
            <span>
              <span className="block text-sm font-medium">Scaffold full trip costs</span>
              <span className="block text-xs text-sand-500">Auto-add round-trip flights (live-priced) and a placeholder stay around the item's dates.</span>
            </span>
          </label>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="btn-swiss btn-ghost text-sm" data-testid="scenario-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-swiss btn-primary text-sm disabled:opacity-50" data-testid="scenario-create-btn">
            {busy ? "Pricing…" : "Create scenario"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
